// @ts-check
// Formatting and date utilities

/**
 * A value that may carry a date: an ISO string, a Date, or nothing.
 * @typedef {string | number | Date | null | undefined} DateLike
 */

/**
 * ID-GEN-002: opaque (non-display) unique ids use crypto.randomUUID() —
 * never a bespoke Math.random()/Date.now() construction. Existing
 * persisted ids remain the old 9-character base36 shape (this is a
 * behavior-preserving swap of what a NEW id looks like, not a migration —
 * any consumer must treat an id as an opaque unique string, never assume
 * a fixed shape).
 * @returns {string} A cryptographically random UUID.
 */
export const generateId = () => crypto.randomUUID();

/** @returns {string} Current time as an ISO-8601 timestamp. */
export const now = () => new Date().toISOString();

// fmtDate is the compliance engine's date formatter (evaluateControl gate
// reasons), so its one definition lives with the engine (GH #7115).
export { fmtDate } from '../../azure-functions/shared/compliance/formatDate.cjs';

/**
 * @param {DateLike} val
 * @returns {number | null} Four-digit year, or null for missing/invalid input.
 */
export const safeYear = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d.getFullYear();
};

/**
 * Format a date as `04 Mar 2026, 14:30`. Returns an em dash for missing input.
 * @param {DateLike} iso
 * @returns {string}
 */
export const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Always returns a boolean — a missing or unparseable due date is never overdue.
 * (An invalid Date compares false, so the guard below is only for null/empty input.)
 * @param {DateLike} dueDate
 * @returns {boolean}
 */
export const isOverdue = (dueDate) => !!dueDate && new Date(dueDate) < new Date();
